//#region Libraries
import { useState } from "react";
import { useNavigate } from "react-router";
//#endregion
//#region Styles
import { Container } from "./styles";
//#endregion
//#region Components
import Preloader from "components/Preloader/Preloader";
import CustomButton from "components/CustomButton/CustomButton";
import DialogAlertSuccessNotify from "components/DialogAlertNotify/DialogAlertSuccessNotify/DialogAlertSuccessNotify";
import DialogAlertAction from "components/DialogAlertAction/DialogAlertAction";
import DialogShowErrorInfoAtTheEndOfCreation from "../DialogShowErrorInfoAtTheEndOfCreation/DialogShowErrorInfoAtTheEndOfCreation";
//#endregion
//#region Utils
import { errorFree } from "utils/validations";
import {
	requestCreateEventMassiveLoading,
	requestValidateFileForCreateEventMassiveLoading,
} from "services/events/aboutEvents";
import { useNavigatingAway } from "utils/hooks/usePrompt";
import useMainContext from "utils/context/useMainContext";
import useLoadingRequest from "components/Preloader/utils/hooks/useLoadingRequest";
import useShowDialogAlert from "components/DialogAlert/utils/hooks/useDialogAlert";
import ABSOLUTE_PATHS from "routes/utils/absolutePaths";
//#endregion

const Footer = ({
	form,
	changeErrors,
	currentStep,
	setMustCorrect,
	errorStack,
	toStep,
	canShowDialogLeavingPage,
}) => {
	//#region Extra
	const navigate = useNavigate();
	const mainContext = useMainContext();
	const loadingRequest = useLoadingRequest();
	//#endregion
	//#region States
	const [showDialogCreatedEvent, setShowDialogCreatedEvent] = useState(false);
	const [
		errorInformationAtTheEndOfCreation,
		setErrorInformationAtTheEndOfCreation,
	] = useState(null);
	//#endregion
	const dialogShowErrorInformationAtTheEndOfCreation = useShowDialogAlert();
	//#region Functions
	//#region Manejo de salida de vista
	const [showDialogLeavingPage, confirmNavigation, cancelNavigation] =
		useNavigatingAway(canShowDialogLeavingPage);
	//#endregion
	const toggleShowDialogCreatedEvent = (value = null) => {
		setShowDialogCreatedEvent(prev => (value === null ? !prev : value));
		if (value === false) toEventList();
	};
	const toEventList = () => {
		setTimeout(() => {
			navigateToEvents();
			confirmNavigation();
		}, 380);
	};
	const navigateToEvents = () => {
		navigate(ABSOLUTE_PATHS.EVENT_LIST);
	};
	//#region Validations
	const isValidForm = () => {
		const { fileData } = form.value;
		const newErrors = {
			fileData: !fileData.base64,
		};
		const isValid = errorFree(newErrors);
		return {
			isValid,
			newErrors,
		};
	};
	//#endregion
	const showMustCorrectMessage = () => {
		setMustCorrect(true);
		setTimeout(() => {
			setMustCorrect(false);
		}, 2400);
	};
	const navigationHandler = async () => {
		const result = isValidForm();
		const { isValid, newErrors } = result;
		changeErrors(newErrors);
		if (!isValid) showMustCorrectMessage();
		else {
			loadingRequest.show("Cargando archivo...");
			try {
				const fetchErrorStack =
					await requestValidateFileForCreateEventMassiveLoading(
						form.value.fileData.base64
					);
				if (fetchErrorStack === null) throw new Error("Request error");
				errorStack.set({
					stack: fetchErrorStack.errorStack,
					rowCountFile: fetchErrorStack.rowCountFile,
				});
				if (fetchErrorStack.errorStack.length === 0) toStep(3);
				else toStep(2);
			} catch (err) {
				mainContext.setShowDialogUnexpectedError(true);
				return;
			} finally {
				loadingRequest.hide();
			}
		}
		window.scrollTo({ top: 0, behavior: "smooth" });
	};
	const finalizeMassiveLoading = async () => {
		loadingRequest.show("Guardando eventos...");
		const res = await requestCreateEventMassiveLoading(
			form.value.fileData.base64
		);
		loadingRequest.hide();
		if (res.success) {
			setShowDialogCreatedEvent(true);
			return;
		}
		const { row, message } = res;
		if (row || message) {
			mainContext.setShowDialogUnexpectedError(true);
			mainContext.setDialogUnexpectedErrorMessage(
				`Fila: ${row} | ${message || "Ocurrió un error inesperado."}`
			);
		} else {
			const { errorStack, quantities } = res;
			setErrorInformationAtTheEndOfCreation({
				errorStack,
				quantities,
			});
			if (quantities.error > 0) form.reset();
			dialogShowErrorInformationAtTheEndOfCreation.toggle();
		}
		toStep(1);
	};
	const getTitleDialogCreateEvent = () => {
		const { rowCountFile } = errorStack.value;
		return `Hemos creado ${rowCountFile} evento${
			rowCountFile > 1 ? "s" : ""
		}`;
	};
	//#endregion
	return (
		<>
			<Container>
				<div>
					<CustomButton
						text="Cancelar"
						variant="sixtinary"
						onClick={() => {
							navigateToEvents();
						}}
					/>
				</div>
				{(currentStep === 1 ||
					(currentStep > 1 &&
						(errorStack.value.stack.length > 0 || errorStack.value.rowCountFile === 0))) && (
					<CustomButton
						text="Continuar"
						onClick={navigationHandler}
					/>
				)}
				{currentStep > 2 && errorStack.value.rowCountFile > 0 && (
					<CustomButton
						text="Finalizar"
						onClick={finalizeMassiveLoading}
					/>
				)}
			</Container>
			<Preloader {...loadingRequest.value} />
			{errorInformationAtTheEndOfCreation && (
				<DialogShowErrorInfoAtTheEndOfCreation
					{...errorInformationAtTheEndOfCreation}
					dialogShow={dialogShowErrorInformationAtTheEndOfCreation}
				/>
			)}
			<DialogAlertAction
				show={showDialogLeavingPage}
				toggleShow={cancelNavigation}
				title="¿Deseas salir sin guardar tu evento?"
				content="Recuerda que al salir, la información que has escrito recientemente, no se guardará"
			>
				<CustomButton
					variant="quintinary"
					text="Sí, salir"
					width="130px"
					onClick={confirmNavigation}
				/>
				<CustomButton
					variant="quaternary"
					text="No, continuar"
					width="130px"
					onClick={cancelNavigation}
				/>
			</DialogAlertAction>
			<DialogAlertSuccessNotify
				show={showDialogCreatedEvent}
				toggleShow={toggleShowDialogCreatedEvent}
				title={getTitleDialogCreateEvent()}
				subtitle={`Podrás verlo en el listado de "Eventos"`}
				description="IMPORTANTE: Recuerda revisar detalladamente toda la información de tus eventos cargados."
				buttons={
					<CustomButton
						variant="quaternary"
						text="Ir al listado de eventos"
						onClick={() => toggleShowDialogCreatedEvent(false)}
					/>
				}
			/>
		</>
	);
};

export default Footer;
